/**
 * Client-side mirror of `selector_quality.py`. Lightweight — same
 * categories + scoring rules so the editor can flag fragile selectors
 * inline without an extra round-trip on every keystroke.
 *
 * Categories: "excellent" | "good" | "fragile" | "broken"
 */
const _AUTO_CLASSES = /\.[a-z]+-[a-z0-9]+-[a-z0-9]{4,}/;
const _INDEXED = /:nth-child|:nth-of-type/;
const _DEEP = /\s>?\s.*\s>?\s/;
const _STRONG_CSS = /(\[role=|\[aria-|\[name=|\[type=)/;

export function scoreSelector(selector) {
  if (!selector || !selector.trim()) {
    return { quality: "broken", score: 0, reason: "Empty selector" };
  }
  const sel = selector.trim();
  if (sel.startsWith("data-testid=") || sel.includes("[data-testid")) {
    return { quality: "excellent", score: 100, reason: "Uses data-testid — most stable" };
  }
  if (sel.includes("aria-label") || sel.startsWith("role=")) {
    return { quality: "excellent", score: 95, reason: "Accessibility attribute (aria-label / role)" };
  }
  if (sel.startsWith("#") && !sel.includes("/") && !_AUTO_CLASSES.test(sel)) {
    return { quality: "good", score: 85, reason: "Element ID" };
  }
  if (sel.startsWith("text=") || sel.includes('text="')) {
    return { quality: "good", score: 80, reason: "Locates by visible text" };
  }
  if (sel.startsWith("/") || sel.startsWith("xpath=")) {
    if (_INDEXED.test(sel) || _DEEP.test(sel)) {
      return { quality: "fragile", score: 30, reason: "Deep XPath with positional indices" };
    }
    return { quality: "fragile", score: 45, reason: "XPath — fragile to DOM shape" };
  }
  if (_INDEXED.test(sel)) {
    return { quality: "fragile", score: 25, reason: "Uses :nth-child / :nth-of-type" };
  }
  if (_AUTO_CLASSES.test(sel)) {
    return { quality: "fragile", score: 20, reason: "Auto-generated CSS classes change every build" };
  }
  if (_DEEP.test(sel)) {
    return { quality: "fragile", score: 35, reason: "Long descendant chain" };
  }
  if (_STRONG_CSS.test(sel)) {
    return { quality: "good", score: 70, reason: "Attribute-based CSS selector" };
  }
  if (sel.length < 60 && (sel.match(/ /g) || []).length <= 1 && !sel.includes(".")) {
    return { quality: "good", score: 65, reason: "Short, semantic CSS" };
  }
  return { quality: "fragile", score: 40, reason: "Generic CSS — consider adding a data-testid" };
}

export const QUALITY_LABEL = {
  excellent: { tint: "#10B981", bg: "#10B98115", label: "Excellent" },
  good: { tint: "#3B82F6", bg: "#3B82F615", label: "Good" },
  fragile: { tint: "#F59E0B", bg: "#F59E0B15", label: "Fragile" },
  broken: { tint: "#EF4444", bg: "#EF444415", label: "Broken" },
};
